import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { Select } from "shared/Select/Select";
import Button from "shared/Button";
import Input from "shared/@forms/Input";

import styles from "./IncidentCommunication.module.css";

import classNames from "classnames";

import emailTemplate from "../../Shared/EmailTemplate/emailTemplate";
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
} from "@react-pdf/renderer";

/**
 *
 * A form to send case-related emails. List of the case’s email history displayed to the right with a btn to preview the sent email.
 *
 */

export function IncidentCommunication({
  communications,
  caseInfo,
  onSubmit,
  openModal,
}) {
  const [sendToSelect, setSendToSelect] = useState("Send To");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [template, setTemplate] = useState("Choose Template");

  //useEffect to handle email templates
  useEffect(() => {
    if (template !== "Choose Template") {
      const getTemplate = emailTemplate(template);
      setSubject(getTemplate.subject);
      setText(getTemplate.body);
    }
  }, [template]);

  // pdf stuff
  const pdfStyles = StyleSheet.create({
    page: {
      display: "flex",
      flexDirection: "row",
    },
    section: {
      marginBottom: "20px",
      padding: "20px",
      display: "flex",
      flexDirection: "column",
    },
    title: {
      textDecoration: "underline",
      marginBottom: "20px",
      marginTop: "20px",
      fontWeight: 100,
    },
    text: {
      whiteSpace: "pre-line",
      lineBreak: "anywhere",
    },
    row: {
      marginBottom: "10px",
    },
    listText: {
      whiteSpace: "pre-line",
      lineBreak: "anywhere",
      marginBottom: "20px",
    },
    listItem: {
      marginBottom: "10px",
    },
  });

  const formattedDateTime = new Date().toLocaleString("en-US", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const PDF = () => (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.text}>Sent: {formattedDateTime}</Text>

          <Text style={pdfStyles.title}>EMAIL</Text>
          <Text style={pdfStyles.text}>To: {sendToSelect}</Text>
          <Text style={pdfStyles.text}>Subject: {subject}</Text>
          <Text style={pdfStyles.text}>Body: {text}</Text>
        </View>
      </Page>
    </Document>
  );

  const [errors, setErrors] = useState({});
  const validate = () => {
    setErrors({});
    var _errors = {};
    if (sendToSelect === "Send To") {
      _errors.sendTo = "Please select a recipient";
    }
    if (template === "Choose from Template") {
      _errors.template = "Please choose a template";
    }

    if (subject.length < 1) {
      _errors.subject = "Please include a subject line";
    }

    if (text.length < 1) {
      _errors.text = "Please include an email body";
    }

    if (Object.keys(_errors).length < 1) {
      onSubmit({
        sendTo: sendToSelect,
        template: template,
        subject: subject,
        emailBody: text,
      });
      clearForm();
    } else {
      setErrors(_errors);
    }
  };

  const clearForm = () => {
    setSendToSelect("Send To");
    setSubject("");
    setText("");
    setTemplate("Choose Template");
  };

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <div className={styles.title}>Send as Communication</div>
        <div className={styles.select}>
          <Select
            placeholder={sendToSelect}
            options={[
              `Primary Contact - ${caseInfo.victim.email}`,
              `Reported Contact -${caseInfo.aggressor.email}`,
              "System Administrator - support@wwdb.com",
            ]}
            onChange={setSendToSelect}
          />
          <span className={styles.error}>{errors.sendTo}</span>
        </div>
        <div className={styles.select}>
          <Select
            placeholder={template}
            options={[
              "Caution Letter",
              "Review Letter",
              "Probation Letter",
              "Disciplinary Letter",
              "Disciplinary Letter - Physical Threat",
              "Withdrawl Letter",
              "Admonish Letter",
              "Reinstated Letter",
              "Case Completion Letter",
            ]}
            onChange={setTemplate}
          />
          <div className={styles.error}>{errors.template}</div>
        </div>
        <div>
          <Input
            className={styles.input}
            id="subject"
            placeholder="Subject Line"
            defaultValue={subject}
            onChange={(e) => {
              setSubject(e.target.value);
            }}
          />
          <div className={styles.error}>{errors.subject}</div>
        </div>

        <div className={styles.textAreaContainer}>
          <textarea
            className={styles.textArea}
            placeholder="Add text here..."
            id="body"
            defaultValue={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className={styles.error}>{errors.text}</div>
        </div>
        <div>
          <div className={styles.actionBar}>
            <PDFDownloadLink
              document={<PDF />}
              fileName="Communication"
              className={styles.cardItem}
            >
              <Button
                className={classNames(styles.button, styles.printPDFButton)}
              >
                Download PDF
              </Button>
            </PDFDownloadLink>

            <Button
              className={classNames(styles.button, styles.saveButton)}
              onClick={validate}
            >
              Send
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.right}>
        <div className={styles.title}>Recent Activities</div>

        <div className={styles.recentActivityContainer}>
          <div className={styles.innerContainer}>
            {communications && communications.length > 0
              ? communications.map((communication, index) => {
                  return (
                    <>
                      <div className={styles.recentActivityDate}>
                        {communication.date}
                      </div>
                      <div className={styles.recentActivityDetails}>
                        {communication.message}
                      </div>
                      <div className={styles.recentActivityButtonContainer}>
                        <Button
                          className={classNames(
                            styles.button,
                            styles.saveButton
                          )}
                          onClick={() => {
                            openModal(index);
                          }}
                        >
                          View
                        </Button>
                      </div>
                    </>
                  );
                })
              : "No activity"}
          </div>
        </div>
      </div>
    </div>
  );
}

IncidentCommunication.propTypes = {
  communications: PropTypes.arrayOf(PropTypes.object),
};
